import { useEffect, useState } from 'react';

interface UseCountAnimationProps {
  end: number;
  duration?: number;
  start?: number;
  shouldAnimate?: boolean;
  delay?: number;
}

const easeOutExpo = (progress: number): number => 
  progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);

export const useCountAnimation = ({ 
  end, 
  duration = 1500, 
  start = 0,
  shouldAnimate = true,
  delay = 0
}: UseCountAnimationProps): number => {
  const [count, setCount] = useState(start);

  useEffect(() => {
    if (!shouldAnimate) {
      setCount(start);
      return;
    }

    const startDelay = setTimeout(() => {
      let startTime: number | null = null;
      let animationFrame: number;

      const animate = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        setCount(Math.floor(easeOutExpo(progress) * (end - start) + start));

        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        }
      };

      animationFrame = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animationFrame);
    }, delay);

    return () => clearTimeout(startDelay);
  }, [end, duration, start, shouldAnimate, delay]);

  return count;
};
